// @ts-check
/**
 * BDHCM-116 (Bakers only) — Hide PLP/PDP prices until a bakery is selected.
 * Syncs `html[data-bakery-selected]` from StoreSelectionState and listens for
 * `store-selection:changed` / `store-selection:cleared`.
 * No-ops on COBS (and any non-`.bakers-store` document).
 */

import '@theme/store-selection-state';

/**
 * @returns {boolean}
 */
function isBakersStore() {
  return Boolean(document.documentElement?.classList?.contains('bakers-store'));
}

/**
 * @returns {boolean}
 */
function hasBakerySelection() {
  const stateApi =
    typeof window !== 'undefined' ? /** @type {any} */ (window).StoreSelectionState : undefined;
  if (stateApi && typeof stateApi.getSelection === 'function') {
    return stateApi.getSelection() != null;
  }

  try {
    const raw = window.localStorage.getItem('amb_store_selection');
    if (!raw) return false;
    const sel = JSON.parse(raw);
    return Boolean(
      sel &&
        typeof sel === 'object' &&
        String(sel.storeId || '').trim() !== '' &&
        String(sel.slug || '').trim() !== '' &&
        String(sel.name || '').trim() !== ''
    );
  } catch (_) {
    return false;
  }
}

/**
 * @param {boolean} selected
 */
export function setBakerySelectedFlag(selected) {
  if (typeof document === 'undefined' || !isBakersStore()) return;
  document.documentElement.dataset.bakerySelected = selected ? 'true' : 'false';
}

/**
 * Syncs the bakery-selected flag from current store selection state.
 */
export function syncPriceVisibility() {
  if (!isBakersStore()) return;
  setBakerySelectedFlag(hasBakerySelection());
}

/**
 * Wire store-selection events so prices show/hide without a full page reload.
 */
function initPriceVisibility() {
  if (!isBakersStore()) return;

  syncPriceVisibility();

  window.addEventListener('store-selection:changed', () => {
    setBakerySelectedFlag(true);
  });

  window.addEventListener('store-selection:cleared', () => {
    setBakerySelectedFlag(false);
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPriceVisibility, { once: true });
  } else {
    initPriceVisibility();
  }
}
